import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { useSelector } from "react-redux";
import { setLoading, setSnackInfo } from "../redux/actions/appAction";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  TablePagination,
  Dialog,
  DialogContent,
  DialogTitle,
  Box
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import HttpService from "../services/httpService";
import { Link } from 'react-router-dom'
import moment from 'moment'
import { Divider } from "antd";
import InfoIcon from '@material-ui/icons/Info';
const useStyles = makeStyles((theme) => ({
  root: {
    padding: "24px",
    width: "100%",
  },
  table: {
    minWidth: 650,
  },
  headerCell: {
    backgroundColor: "#1C375D",
    color: "white",
    fontWeight: "bold",
    textAlign: "center",
    // border: "1px solid #fff",
  },
  tableCell: {
    textAlign: "center",
    border: "1px solid #e0e0e0",
  },
  title: {
    padding: "16px 24px",
    backgroundColor: "#f5f5f5",
    borderBottom: "1px solid #e0e0e0",
    fontWeight: "bold",
    textDecoration: 'underline'
  },
  pagination: {
    borderTop: "1px solid #e0e0e0",
  },
  clearButton: {
    backgroundColor: '#f7b7b7 !important',
    color: '#151921 !important',
  },
  submitButton: {
    backgroundColor: '#F47E46 !important',
    color: 'white !important',
    '&:hover': {
      backgroundColor: '#E0663D !important',
    },
  },
}));

const CommitmentTasks = ({ setLoading, setSnackInfo }) => {
  const classes = useStyles();
  const { userDetails } = useSelector((state) => state.user);

  // Get current date in YYYY-MM-DD format
  const getCurrentDate = () => {
    return new Date().toISOString().split("T")[0];
  };

  // Set default start date as 2022-01-01
  const getDefaultStartDate = () => {
    return "2022-01-01";
  };

  const [designFilters, setDesignFilters] = useState({
    status: "Active + IDSO Marked Completed", // Default status
    startDate: getDefaultStartDate(),
    endDate: getCurrentDate(),
  });

  const [pagination, setPagination] = useState({
    page: 0,
    limit: 10,
    total: 0,
    totalPages: 0,
  });

  const [dashboardData, setDashboardData] = useState({
    currentMonth: "",
    recentFlags: [],
    designprojectsdashboard: [],
  });

  const [showLatestNotes ,setShowLatestNotes] = useState(false);
  const [leadData, setLeadData] = useState({});

  useEffect(() => {
    if (userDetails._id) {
      fetchDashboardData();
      fetchDesignData(); // Fetch initial design data with default dates and status
    }
  }, [userDetails]);

  // Fetch first table data (no filters)
  const fetchDashboardData = async () => {
    if (!userDetails._id) return;

    setLoading(true);
    try {
      const response = await HttpService.currentMonthDso();

      if (response.data && response.data.success) {
        const flaggedProjects = Array.isArray(response.data.data)
          ? response.data.data
          : [];

        setDashboardData((prev) => ({
          ...prev,
          currentMonth: response.data.currentMonth,
          recentFlags: flaggedProjects,
        }));
      }
    } catch (error) {
      setSnackInfo(
        error?.response?.data?.message || "Failed to fetch dashboard data",
        "error"
      );
    }
    setLoading(false);
  };

  // Fetch second table data with filters and pagination
  const fetchDesignData = async (
    filters = designFilters,
    page = pagination.page,
    limit = pagination.limit
  ) => {
    if (!userDetails._id) return;

    setLoading(true);
    try {
      // Prepare filter parameters for API call - matching your API format
      const filterParams = {
        status: filters.status || "Active + IDSO Marked Completed", // Default status
        startDate: filters.startDate || getDefaultStartDate(),
        endDate: filters.endDate || getCurrentDate(),
        page: page + 1, // Backend expects 1-based page number
        limit: limit,
      };



      // Call API with filters - using your exact API method format
      const designResponse = await HttpService.designprojectsdashboard(
        filterParams
      );

      if (designResponse.data && designResponse.data.success) {
        setDashboardData((prev) => ({
          ...prev,
          designprojectsdashboard: designResponse.data.data || [],
        }));

        // Update pagination info from API response
        if (designResponse.data.pagination) {
          setPagination((prev) => ({
            ...prev,
            total: designResponse.data.pagination.totalItems,
            totalPages: designResponse.data.pagination.totalPages,
          }));
        }
      }
    } catch (error) {
      console.error("API Error:", error);
      setSnackInfo(
        error?.response?.data?.message || "Failed to fetch design projects",
        "error"
      );
    }
    setLoading(false);
  };

  const handleDesignFilterChange = (filterName, value) => {
    setDesignFilters((prev) => ({
      ...prev,
      [filterName]: value,
    }));
  };

  const applyDesignFilters = () => {
    // Reset to first page when applying new filters
    setPagination((prev) => ({ ...prev, page: 0 }));
    // Call API with current filters and page 0
    fetchDesignData(designFilters, 0);
  };

  const clearDesignFilters = () => {
    const defaultFilters = {
      status: "Active + IDSO Marked Completed", // Reset to default status
      startDate: getDefaultStartDate(),
      endDate: getCurrentDate(),
    };
    setDesignFilters(defaultFilters);
    // Reset pagination and fetch data with default filters
    setPagination((prev) => ({ ...prev, page: 0 }));
    fetchDesignData(defaultFilters, 0);
  };

  // Handle page change
  const handlePageChange = (event, newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
    fetchDesignData(designFilters, newPage);
  };

  // Handle rows per page change
  const handleRowsPerPageChange = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setPagination((prev) => ({
      ...prev,
      limit: newLimit,
      page: 0, // Reset to first page
    }));
    fetchDesignData(designFilters, 0, newLimit);
  };

  // handle last communication dialog function
  const showNotesInfo = (data)=>{
    setShowLatestNotes(true);
    setLeadData(data);
  }

  return (
    <div className={classes.root}>
      {/* First Table - Projects Committed Status (No Filters) */}
      <Paper elevation={2} style={{ marginBottom: "24px", textAlign:"center",fontWeight: "bold",}}>
        <Typography variant="h5" className={classes.title}>
          Projects Committed Status {dashboardData.currentMonth}
        </Typography>
        <TableContainer>
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell className={classes.headerCell}>Sr. No</TableCell>
                <TableCell className={classes.headerCell}>Lead No.</TableCell>
                <TableCell className={classes.headerCell}>Project No.</TableCell>
                <TableCell className={classes.headerCell}> Customer Name</TableCell>
                <TableCell className={classes.headerCell}>Current stage</TableCell>
                <TableCell className={classes.headerCell}>Last Communication Date</TableCell>
                <TableCell className={classes.headerCell}>Days pending to complete DSO</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dashboardData.recentFlags.length > 0 ? (
                dashboardData.recentFlags.map((project, idx) => (
                  <TableRow key={project.id || idx} style={{ backgroundColor: project?.internalDesignSignOffDate ? '#d4edda' : 'transparent'}} >
                    <TableCell className={classes.tableCell}>
                      {idx + 1}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                    <Link to={`/project-logs/${project.id}`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline', color: '#1C375D', fontWeight: 700 }}> {project.lead_no|| "N/A"}</Link>
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                    <Link to={`/project-logs/${project.id}`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline', color: '#1C375D', fontWeight: 700 }}> {project.projectNo || "N/A"}</Link>
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {project.customerName || "N/A"}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {project.currentStage || "N/A"}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {project.lastCommunicationDate || "N/A"}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {project.daysPendingToCompleteDSO || "N/A"}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    align="center"
                    className={classes.tableCell}
                  >
                    No commitment tasks found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Second Table - Design Project Dashboard (With Filters) */}
      <Paper>
        {/* Filters for Second Table */}
        <div style={{ padding: "16px" , marginTop: '80px'}}>
          {/* Title with spacing below */}
          <Typography
            variant="h5"
            style={{
              margin: "16px 5px",
              textAlign: "center",
              fontWeight: "bold",
              marginBottom: "32px",
              textDecoration: 'underline' 
            }}
          >
            Design Project Dashboard
          </Typography>

          {/* Grid container */}
          <Grid container spacing={3} alignItems="center">
            {/* Status Filter */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth variant="outlined">
                <InputLabel id="status-label">Select Status</InputLabel>
                <Select
                  labelId="status-label"
                  value={designFilters.status}
                  label="Select Status"
                  onChange={(e) =>
                    handleDesignFilterChange("status", e.target.value)
                  }
                >
                  <MenuItem value="Active + IDSO Marked Completed">
                    Active + IDSO Marked Completed
                  </MenuItem>
                  <MenuItem value="All">All</MenuItem>
                  <MenuItem value="Design">Design</MenuItem>
                  <MenuItem value="On-Hold">On-Hold</MenuItem>
                  <MenuItem value="IDSO Marked as Completed">
                    IDSO Marked as Completed
                  </MenuItem>
                  <MenuItem value="KOM Pending">KOM Pending</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Date Filters Grouped Together */}
            <Grid item xs={12} sm={6} md={6}>
              <Grid container spacing={2}>
                {/* Start Date - Takes half of the space */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Sales Won Start Date"
                    type="date"
                    variant="outlined"
                    value={designFilters.startDate}
                    onChange={(e) =>
                      handleDesignFilterChange("startDate", e.target.value)
                    }
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                      max: new Date().toISOString().split("T")[0],
                    }}
                  />
                </Grid>

                {/* End Date - Takes half of the space */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Sales Won End Date"
                    type="date"
                    variant="outlined"
                    value={designFilters.endDate}
                    onChange={(e) =>
                      handleDesignFilterChange("endDate", e.target.value)
                    }
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Buttons */}
            <Grid
              item
              xs={12}
              sm={12}
              md={3}
              style={{ display: "flex", gap: "8px", justifyContent: { xs: "flex-start", md: "flex-end" } }}
            >
              <Button
                variant="contained"
                className={classes.submitButton}
                onClick={applyDesignFilters}
                fullWidth={{ xs: true, sm: false }}
              >
                Submit
              </Button>
              <Button
                variant="contained"
                className={classes.clearButton}
                onClick={clearDesignFilters}
                fullWidth={{ xs: true, sm: false }}
              >
                Clear
              </Button>
            </Grid>
          </Grid>
        </div>

        {/* Second Table */}
        <TableContainer>
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
              <TableCell colSpan={13}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-end",
                    width: "100%",
                  }}
                >
                  <Box style={{ textAlign: "left" }}>
                    <b>Design Quotation Value is less Sales Quotation Value.</b>

                    <Box
                      style={{
                        display: "flex",
                        gap: "10px",
                        marginTop: "12px",
                      }}
                    >
                      {[
                        { percent: "11-20%", color: "yellow" },
                        { percent: ">20%", color: "red" },
                      ].map((ele, index) => (
                        <Box
                          key={index}
                          style={{
                            borderRadius: "7px",
                            width: "70px",
                            height: "20px",
                            backgroundColor: ele.color,
                            textAlign: "center",
                            lineHeight: "20px",
                          }}
                        >
                          <b>{ele.percent}</b>
                        </Box>
                      ))}
                    </Box>
                  </Box>

                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={pagination.total}
                    rowsPerPage={pagination.limit}
                    page={pagination.page}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    SelectProps={{
                      inputProps: { "aria-label": "rows per page" },
                      native: true,
                    }}
                    labelDisplayedRows={({ from, to, count }) =>
                      `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
                    }
                  />
                </div>
              </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={classes.headerCell}>Sr. No</TableCell>
                <TableCell className={classes.headerCell}>Project No</TableCell>
                <TableCell className={classes.headerCell}>Lead No.</TableCell>
                <TableCell className={classes.headerCell}>
                  Client Name
                </TableCell>
                <TableCell className={classes.headerCell}>
                  Sales Quotation Value (Lacs)
                </TableCell>
                <TableCell className={classes.headerCell}>
                  Sales Won Date
                </TableCell>
                <TableCell className={classes.headerCell}>Status</TableCell>
                <TableCell className={classes.headerCell}>
                  Design Start Date
                </TableCell>
                <TableCell className={classes.headerCell}>Ageing</TableCell>
                <TableCell className={classes.headerCell}>
                  Tentative Design Sign Off Date
                </TableCell>
                <TableCell className={classes.headerCell}>
                  Design Value (Lacs)
                </TableCell>
                <TableCell className={classes.headerCell}>
                  Last Communication Date
                </TableCell>
                <TableCell className={classes.headerCell}>
                  Last Communication Message
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dashboardData.designprojectsdashboard.length > 0 ? (
                dashboardData.designprojectsdashboard.map((project, idx) => (
                  <TableRow key={project.id || idx}>
                    <TableCell className={classes.tableCell}>
                      {project.srNo || idx + 1}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                    <Link to={`/project-logs/${project.id}`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline', color: '#1C375D', fontWeight: 700 }}>
                      {project?.projectNo || "N/A"}</Link>
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                    <Link to={`/project-logs/${project.id}`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline', color: '#1C375D', fontWeight: 700 }}>
                      {project?.leadNo || "N/A"}</Link>
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {project?.clientName || "N/A"}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {project?.salesQuotationValue ? `${(project.salesQuotationValue / 100000).toFixed(2)}`: "N/A"}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {project?.salesWonDate || "N/A"}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {project?.status || "N/A"}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {project?.designStartDate || "N/A"}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {project?.ageing || "N/A"}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {project?.tentativeDesignSignOffDate || "N/A"}
                    </TableCell>
                    <TableCell style={{backgroundColor:project.designValueStatus}} className={classes.tableCell}>
                      {project?.designValue ? `${(project.designValue / 100000).toFixed(2)}`: "N/A"}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {project?.lastCommunicationDate ? moment(project.lastCommunicationDate).format('DD-MM-YYYY'): 'N/A' }
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      <InfoIcon style={{ cursor: 'pointer', color: "#666" }} onClick={() => showNotesInfo(project)} />
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={13}
                    align="center"
                    className={classes.tableCell}
                  >
                    No design projects found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* show latest notes modal */}
      <Dialog
        open={showLatestNotes}
        onClose={() => setShowLatestNotes(false)}
        aria-labelledby="form-dialog-title"
        fullWidth
        maxWidth='sm'
      >
        <DialogTitle id="form-dialog-title">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>Latest Notes </span>
              <span style={{ fontSize: "1rem", color: "#666" }}>
                    {leadData?.leadNo ? leadData?.leadNo  : ""}
              </span>
          </div>
        </DialogTitle>
        <Divider />
        <DialogContent style={{ marginBottom: "18px" }}>
          {leadData?.lastCommunicationMessage || "No Notes"}
        </DialogContent>
      </Dialog>

    </div>
  );
};

const mapDispatchToProps = {
  setLoading,
  setSnackInfo,
};

export default connect(null, mapDispatchToProps)(CommitmentTasks);
